import { Injectable } from '@angular/core';
import {
    CurrentModuleOptions,
    errorPageQueryString,
    ModuleUserAccessLevel,
    PageErrorType
} from '../constants-types';
import { PI } from '../constants-types/PI.model';
import { InitData } from '../core-data-model';
import { UserProfileApi } from '../core-services/UserProfile/user-profile-api.connnect';
import { QueryStringUtility } from '../core-utilities/QueryString.utility';
import { AppType } from '../shared';
import { ConfigurationService } from '../system-configuration';
import {
    ApplicationInitializedMessage,
    DemoDataService,
    FeatureFlagsService,
    InitDataService,
    MessagingService,
    ModuleAccessTransformer
} from '../system-services';
import { ModuleAccessService } from '../system-services/ModuleAccess/ModuleAccess.service';
import { UserProfileSettingsService } from '../system-services/UserProfileSettings/UserProfileSettings.service';
import { TranslationDataService } from '../translations/Translation.dataservice';
@Injectable({
    providedIn: 'root'
})
export class AppBootstrapHelpersService {
    constructor(
        private translations: TranslationDataService,
        private queryStringUtil: QueryStringUtility,
        private moduleAccess: ModuleAccessService,
        private userProfile: UserProfileSettingsService,
        private initDataService: InitDataService,
        private messagingService: MessagingService,
        private moduleAccessTransform: ModuleAccessTransformer,
        private config: ConfigurationService,
        private featureFlagService: FeatureFlagsService,
        private demoDataService: DemoDataService,
        private userProfileApi: UserProfileApi
    ) {}

    async bootstrapInitDataAndModuleAccess(): Promise<InitData> {
        const initData = await this.buildInitData();
        this.userProfile.setup();
        await this.moduleAccess.fetchConfig();

        this.performRedirectIfPerformOnly();
        this.syncLanguageCookieWithProfile();

        this.featureFlagService.initLaunchDarkly(
            this.initDataService,
            this.userProfile,
            this.moduleAccess
        );

        await this.featureFlagService.completionPromise;

        const isDemoEnabled = this.demoDataService.isDemoEnabled();
        if (isDemoEnabled) {
            await this.demoDataService.applyDemoDataExperience();
        }

        // used by the auth route guards
        this.messagingService.sendMessage(new ApplicationInitializedMessage());

        const softwareModuleAccessConfig =
            this.moduleAccess.moduleAccessConfig.softwareModuleAccessConfig;
        initData.softwareModuleAccessConfig = softwareModuleAccessConfig
            ? this.moduleAccessTransform.configureClientToRum(
                  softwareModuleAccessConfig
              ).softwareModuleAccessRUMConfig
            : null;

        return initData;
    }

    async fetchAndLoadTranslations(): Promise<any> {
        const translationsResponse = await this.fetchTranslations();
        const translationsContent = await translationsResponse.text();
        this.translations.data =
            this.evaluateTranslationsResponse(translationsContent);
    }

    /**
     * This function will load the sprite sheets, append them to the dom, and fetch the translations
     * and load them in the TranslationService.
     *
     * The `baseUrl` can be provided for local development purposes. This is
     * set to the pi-spa URL by default to assist with static apps, and because
     * most of the time, it will be using pi-spa in the associated deployed env.
     * The only time this needs to be provided is with local development in
     * theory, where there will be CORS issues otherwise.
     */
    async loadSpriteSheetsAndTranslations(appName?: AppType): Promise<void> {
        // Parallelize these calls to make them faster
        const [
            translationsContent,
            iconSpriteContent,
            referencePatternContent
        ] = await Promise.all([
            (await this.fetchTranslations()).text(),
            (await this.fetchIconSprite()).text(),
            (await this.fetchReferencePatternSprite()).text()
        ]);

        this.appendSvgStringToDom(iconSpriteContent);
        this.appendSvgStringToDom(referencePatternContent);
        this.translations.data =
            this.evaluateTranslationsResponse(translationsContent);

        if (appName) {
            await this.loadAndAppendSpecificSprite(appName);
        }
    }

    async loadAndAppendSpecificSprite(appName: AppType) {
        // Do the same for app specific icon sprite sheet
        const appSpecificIconSprite =
            await this.fetchAppSpecificIconSprite(appName);
        this.appendSvgStringToDom(await appSpecificIconSprite.text());
    }

    // Adding this here to make it so we don't need to also import config service where
    // this is called
    getLogRocketEnabled(): boolean {
        return this.config.getLogRocketEnabled();
    }

    // If the user is not an appropriate module level admin and is not a third-party user,
    // redirect them to Perform if they made it to PI2.
    private performRedirectIfPerformOnly() {
        if (
            this.moduleAccess.hasOnlyPerform &&
            !this.isPerformAdmin() &&
            !this.userProfile.isUserThirdPartyOrHasSuperAdminAccess()
        ) {
            this.redirectToPerform();
        }
    }

    private redirectToPerform() {
        window.location.assign(
            new URL('/token_auth/pi_auth0_login', PI.appSettings.performUrl)
        );
    }

    private isPerformAdmin(): boolean {
        return (
            this.initDataService.userAccessLevelMap.get(
                CurrentModuleOptions.Perform
            )?.userAccessLevel >= ModuleUserAccessLevel.Admin
        );
    }

    private async buildInitData(): Promise<InitData> {
        try {
            return await this.initDataService.getJavaScriptInitData();
        } catch (e) {
            const error: PageErrorType = 'outOfSyncUser';
            window.location.href = `/error?${errorPageQueryString}=${error}`;
        }
    }

    // The language cookie (piLanguageLocaleSpaKey) can fall out of sync with the
    // user's saved language preference (User.LanguageId) -- e.g. a user changed
    // their language before it was ever persisted to the backend. Reconcile them
    // here by pushing the cookie's language back to the backend when they differ.
    //
    // PI2 accounts persist their language choice directly via the global-nav
    // language switcher (language-selection-menu.component.ts), which updates
    // the profile but never touches the cookie. Reconciling against the cookie
    // for those accounts would overwrite that freshly saved preference with a
    // stale cookie value on the next reload, so this only applies to legacy
    // (non-PI2) accounts, which only ever persisted their language to the cookie.
    private syncLanguageCookieWithProfile(): void {
        if (
            !this.initDataService.isInitDataLoaded ||
            this.initDataService.account.piVersion === 2
        ) {
            return;
        }

        const cookieLocale = PI.language?.locale;
        const profileLocale = this.initDataService.profile.defaultLanguageCode;

        if (cookieLocale && cookieLocale !== profileLocale) {
            this.userProfileApi
                .updateUserLanguage({ languageLocale: cookieLocale })
                .subscribe();
        }
    }

    // NOTE: We have a fetch polyfill included so this might be fine
    // The Angular HttpClient was throwing errors because we have a
    // ".json" extension on this endpoint but it's actually javascript.
    // We should make it either have a `.js` extension or make it just
    // actually JSON.
    private async fetchTranslations() {
        // Our code needs to maintain the ability to base language off of
        // query string to make image service work
        let drupalCode = PI.language.drupalCode;

        // initDataService optionally checked due to dependency on image service.
        if (
            this.initDataService.isInitDataLoaded &&
            this.initDataService.account.piVersion === 2
        ) {
            const allowableLanguages = PI.productLanguageMap.FullSite.filter(
                (x) => x.isAvailableForPI2
            ).map((x) => x.languageLocaleCode);

            const userLanguage =
                this.initDataService.profile.defaultLanguageCode;

            const languageCode = allowableLanguages.includes(userLanguage)
                ? userLanguage
                : 'en-US';

            drupalCode = this.getDrupalCode(languageCode);
        }

        const queryLanguage =
            this.queryStringUtil.fetchQueryParameterValue('languageLocale');

        if (queryLanguage) {
            const queryDrupalCode = this.getDrupalCode(queryLanguage);

            if (queryDrupalCode) {
                drupalCode = queryDrupalCode;
            }
        }

        const { translationsCdn, translationVersion } = PI.appSettings;

        const makeUrl = (lang: string) =>
            `${translationsCdn}/translations.${lang}.json?v=${translationVersion}`;

        const translationCode = (drupalCode ?? 'en').toLowerCase();

        let response = await fetch(makeUrl(translationCode), { method: 'GET' });

        if (!response.ok && translationCode !== 'en') {
            response = await fetch(makeUrl('en'), { method: 'GET' });
        }

        return response;
    }

    private getDrupalCode(languageCode): string {
        let drupalCode = '';

        if (languageCode) {
            // Using DoNotConsiderAvailability to always grab the drupalCode if one exists for the language
            // Note: Reports from the Image Service may support more/different languages than the site's general UI
            // eslint-disable-next-line no-underscore-dangle
            const translation = (
                window as any
            ).__productLanguageMap.DoNotConsiderAvailability.find(
                (langInfo) =>
                    languageCode.toLowerCase() ===
                    langInfo.languageLocaleCode.toLowerCase()
            );
            if (translation) {
                drupalCode = translation.drupalCode;
            }
        }

        return drupalCode;
    }

    private fetchIconSprite() {
        return fetch(`/Assets/pi-icon-sprite.svg`, {
            method: 'GET'
        });
    }

    private fetchAppSpecificIconSprite(appName: AppType) {
        /* To load the app specific pi-icon-sprite.svg file we're prefixing the app name.
            For instance, Hire app will need to load /hireAssets/pi-hire-icon-sprite.svg */

        const staticAssetsAppName = AppType[appName].toLowerCase();
        return fetch(
            `/${staticAssetsAppName}Assets/pi-${staticAssetsAppName}-icon-sprite.svg`,
            {
                method: 'GET'
            }
        );
    }

    private fetchReferencePatternSprite() {
        // Define headers so that if the sprite sheet is new on the server,
        // that is pulled down instead of the cached version.
        const headers = new Headers();
        headers.append('pragma', 'no-cache');
        headers.append('cache-control', 'no-cache');

        return fetch(`/Assets/ReferencePatterns/reference-pattern-sprite.svg`, {
            method: 'GET',
            headers: headers
        });
    }

    private evaluateTranslationsResponse(
        translationsResponseText: string
    ): any {
        const translationFilePrefix = 'var Translations=';
        let library: any;
        if (translationsResponseText.startsWith(translationFilePrefix)) {
            library = JSON.parse(
                translationsResponseText.slice(translationFilePrefix.length)
            );
        } else {
            library = JSON.parse(translationsResponseText);
        }
        return {
            ...library,
            key: function (keyName: string) {
                return this[keyName.toLowerCase()];
            }
        };
    }

    private appendSvgStringToDom(svgString: string): void {
        const svgWrapper = document.createElement('div');
        svgWrapper.innerHTML = svgString;
        document.body.appendChild(svgWrapper);
    }
}
